import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { useDispatch, useSelector } from '../../../Flux/index.ts'
import type { State } from '../../../Flux/Slice/index.ts'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DroppedDraft } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import {
  useActiveDraft,
  useActiveFileSummary,
  useDropNotice,
  useDraftsNewestFirst,
  useDroppedDrafts,
  useUnrestorableDrafts,
  useWizardStorageRestored
} from '../../../Flux/Selector/OrderWizard/OrderWizardSelectors.ts'

import { defaultDraftName } from '../../../Services/OrderWizard/wizardFiles.ts'
import { SlimDropBar } from './WizardDropzone.tsx'
import { AddFilesProvider } from './AddFilesContext.ts'
import { WizardDropOverlay } from './WizardDropOverlay.tsx'
import { WizardHeader } from './WizardHeader.tsx'
import { WizardSteps } from './WizardSteps.tsx'
import { DiscardedNotice, Notice, ResumeStatus, UnrestorableNotice } from './ResumeNotices.tsx'
import { StartScreen } from './StartScreen.tsx'
import { useWizardEntry } from './useWizardEntry.ts'
import { draftToOpen } from '../../../Services/OrderWizard/resume.ts'

type RootState = typeof State

const DROP_REASON_TEXT: Record<DroppedDraft['reason'], string> = {
  'order-missing': 'its order no longer exists',
  'order-submitted': 'its order has been submitted'
}

/** Restored drafts that their order ruled out, each said once, with the way to the order where it still exists. */
export const DroppedDraftsNotice = ({ dropped, onSeen }: { dropped: DroppedDraft[]; onSeen: () => void }) =>
  dropped.length === 0 ? null : (
    <Notice testId="wizard-dropped-drafts">
      {dropped.map((entry) => (
        <span key={entry.draftId} className="block">
          Your previous wizard draft “{entry.name}” could not be restored: {DROP_REASON_TEXT[entry.reason]}
          {entry.reason === 'order-submitted' && entry.orderId && (
            <>
              {' '}
              (
              <Link className="link" to={`/order/${entry.orderId}`}>
                follow it in the order view
              </Link>
              )
            </>
          )}
          .
        </span>
      ))}
      <button type="button" className="btn btn-link btn-xs px-0" data-testid="wizard-dropped-drafts-dismiss" onClick={onSeen}>
        Dismiss
      </button>
    </Notice>
  )

export const OrderWizardPage = () => {
  const dispatch = useDispatch()
  const user = useGetAuthenticatedUser()
  const restored = useWizardStorageRestored()
  const drafts = useDraftsNewestFirst()
  const draft = useActiveDraft()
  const unrestorable = useUnrestorableDrafts()
  const dropped = useDroppedDrafts()
  const notice = useDropNotice()
  const startFresh = useSelector((state: RootState) => !!state.orderWizard.startFresh)
  const signedIn = !!user

  useEffect(() => {
    if (signedIn && !restored) {
      dispatch(wizard.restoreRequested())
    }
  }, [signedIn, restored, dispatch])

  const newestDraftId = drafts[0]?.id
  useEffect(() => {
    if (!restored || draft) {
      return
    }
    const open = draftToOpen(newestDraftId, startFresh)
    if (open === 'create') {
      dispatch(wizard.draftCreated({ draftId: uuidv4(), name: defaultDraftName(new Date()), createdAt: new Date().toISOString() }))
    } else {
      dispatch(wizard.activeDraftSet({ draftId: open.activate }))
    }
  }, [restored, draft, newestDraftId, startFresh, dispatch])

  useWizardEntry(draft)

  const needsReadd = useActiveFileSummary()?.needsReadd ?? 0

  if (!draft) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner" />
      </div>
    )
  }

  const startNew = () =>
    dispatch(wizard.draftCreated({ draftId: uuidv4(), name: defaultDraftName(new Date()), createdAt: new Date().toISOString() }))

  const otherDrafts = drafts.filter((other) => other.id !== draft.id && (other.orderId || other.fileOrder.length > 0))
  const header = <WizardHeader draft={draft} onStartNew={startNew} notice={notice} otherDrafts={otherDrafts} />

  return (
    <AddFilesProvider>
      <div className="mx-auto w-full max-w-5xl space-y-3" data-testid="order-wizard">
        <WizardDropOverlay draft={draft} />
        <UnrestorableNotice entries={unrestorable} />
        <DiscardedNotice />
        <ResumeStatus />
        <DroppedDraftsNotice dropped={dropped} onSeen={() => dispatch(wizard.droppedDraftsSeen())} />
        {draft.restored && draft.orderId && needsReadd > 0 && (
          <Notice testId="wizard-recovery-notice">
            This draft&apos;s order exists. Uploads that were in flight must be re-added: drop the whole folder again — files already
            uploaded are skipped — or continue in the classic view.
          </Notice>
        )}
        {draft.orderId ? (
          <WizardSteps
            draft={draft}
            orderId={draft.orderId}
            header={header}
            dropzone={<SlimDropBar draft={draft} prompt={needsReadd > 0 ? 'Drop the whole folder again (files already uploaded are skipped)' : undefined} />}
          />
        ) : (
          <>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-2 sm:px-6">{header}</div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
              <StartScreen draft={draft} />
            </div>
          </>
        )}
      </div>
    </AddFilesProvider>
  )
}
